import { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Receipt,
  Loader2,
  FileText,
  Calendar,
  DollarSign,
  ChevronRight,
  CheckCircle2,
  Clock,
  AlertCircle,
  CalendarClock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { useStripeInvoices } from "@/providers/StripeInvoiceProvider";
import { InvoiceDto, InvoiceState } from "@shared/types/InvoiceDto";

function formatDate(date: string): string {
  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
}

function getDaysUntilDue(invoice: InvoiceDto): number {
  const createdDate = new Date(invoice.createdAt);
  const dueDate = new Date(createdDate);
  dueDate.setDate(dueDate.getDate() + 30);
  return Math.ceil((dueDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
}

function getStatusConfig(status: InvoiceState) {
  switch (status) {
    case "PAID":
      return {
        label: "Paid",
        icon: CheckCircle2,
        className: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
      };
    case "PENDING":
      return {
        label: "Pending",
        icon: Clock,
        className: "bg-amber-500/10 text-amber-500 border-amber-500/20",
      };
    case "UNPAID":
      return {
        label: "Unpaid",
        icon: AlertCircle,
        className: "bg-red-500/10 text-red-500 border-red-500/20",
      };
  }
}

function InvoiceItem({ invoice }: { invoice: InvoiceDto }) {
  const statusConfig = getStatusConfig(invoice.status);
  const StatusIcon = statusConfig.icon;
  const paidPercentage =
    invoice.amount > 0 ? (invoice.paid / invoice.amount) * 100 : 0;
  const daysUntilDue = getDaysUntilDue(invoice);

  return (
    <Link
      to={`/invoices/${invoice.id}/overview`}
      state={{ invoice }}
      className="block border border-border/50 rounded-lg p-4 hover:bg-card/50 hover:border-primary/30 transition-all cursor-pointer group"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
            <FileText className="h-5 w-5 text-primary" />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-medium text-sm text-foreground truncate">
                {invoice.name}
              </h3>
              <Badge
                variant="outline"
                className={`shrink-0 text-[10px] ${statusConfig.className}`}
              >
                <StatusIcon className="h-3 w-3 mr-1" />
                {statusConfig.label}
              </Badge>
            </div>

            <p className="text-xs text-muted-foreground mb-2 line-clamp-1">
              {invoice.description}
            </p>

            <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {formatDate(invoice.createdAt)}
              </span>
              <span className="flex items-center gap-1 font-medium text-foreground">
                <DollarSign className="h-3 w-3" />
                {formatCurrency(invoice.amount)}
              </span>
              {invoice.status !== "PAID" && daysUntilDue <= 10 && (
                <span
                  className={`flex items-center gap-1 ${daysUntilDue <= 3 ? "text-red-400" : "text-amber-400"}`}
                >
                  <CalendarClock className="h-3 w-3" />
                  {daysUntilDue <= 0
                    ? "Due today"
                    : `Due in ${daysUntilDue} days`}
                </span>
              )}
              {invoice.paid > 0 && invoice.paid < invoice.amount && (
                <span className="text-emerald-500">
                  {formatCurrency(invoice.paid)} paid (
                  {paidPercentage.toFixed(0)}%)
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <ChevronRight className="h-5 w-5 text-muted-foreground" />
        </div>
      </div>
    </Link>
  );
}

interface InvoiceSectionProps {
  title: string;
  icon: React.ElementType;
  invoices: InvoiceDto[];
  emptyMessage: string;
  iconClassName?: string;
}

function InvoiceSection({
  title,
  icon: Icon,
  invoices,
  iconClassName,
}: InvoiceSectionProps) {
  if (invoices.length === 0) {
    return null;
  }

  return (
    <Card className="p-0 overflow-hidden">
      <div className="flex items-center gap-3 px-5 py-4 border-b border-border/50 bg-card/50">
        <div
          className={`w-8 h-8 rounded-lg flex items-center justify-center ${iconClassName || "bg-primary/10"}`}
        >
          <Icon className={`h-4 w-4 ${iconClassName ? "" : "text-primary"}`} />
        </div>
        <div>
          <h2 className="font-semibold text-sm">{title}</h2>
          <p className="text-xs text-muted-foreground">
            {invoices.length} invoice{invoices.length !== 1 ? "s" : ""}
          </p>
        </div>
      </div>
      <div className="p-3 space-y-2">
        {invoices.map((invoice, index) => (
          <motion.div
            key={invoice.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.03 }}
          >
            <InvoiceItem invoice={invoice} />
          </motion.div>
        ))}
      </div>
    </Card>
  );
}

export function InvoicesTab() {
  const { invoices, isLoading, error, fetchInvoices } = useStripeInvoices();

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const { upcoming, unpaid, paid } = useMemo(() => {
    const upcoming: InvoiceDto[] = [];
    const unpaid: InvoiceDto[] = [];
    const paid: InvoiceDto[] = [];

    invoices.forEach((invoice) => {
      if (invoice.status === "PAID") {
        paid.push(invoice);
      } else {
        const daysUntilDue = getDaysUntilDue(invoice);
        if (daysUntilDue <= 10) {
          upcoming.push(invoice);
        } else {
          unpaid.push(invoice);
        }
      }
    });

    upcoming.sort((a, b) => getDaysUntilDue(a) - getDaysUntilDue(b));
    unpaid.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
    paid.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );

    return { upcoming, unpaid, paid };
  }, [invoices]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-16">
        <p className="text-destructive mb-4">{error}</p>
        <Button variant="outline" onClick={fetchInvoices}>
          Try Again
        </Button>
      </div>
    );
  }

  if (invoices.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mx-auto mb-4">
          <Receipt className="h-8 w-8 text-muted-foreground/50" />
        </div>
        <p className="font-medium">No invoices yet</p>
        <p className="text-sm text-muted-foreground mt-1">
          Your invoices will appear here once work orders are billed
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <InvoiceSection
        title="Upcoming"
        icon={CalendarClock}
        invoices={upcoming}
        emptyMessage="No invoices due soon"
        iconClassName="bg-amber-500/10 text-amber-500"
      />

      <InvoiceSection
        title="Unpaid"
        icon={AlertCircle}
        invoices={unpaid}
        emptyMessage="No unpaid invoices"
        iconClassName="bg-red-500/10 text-red-400"
      />

      <InvoiceSection
        title="Paid"
        icon={CheckCircle2}
        invoices={paid}
        emptyMessage="No paid invoices"
        iconClassName="bg-emerald-500/10 text-emerald-500"
      />
    </div>
  );
}
